// src/rentals-api-v3/models/AgentRegionSettings.ts
function AgentRegionSettingsFromJSON(json) {
  return AgentRegionSettingsFromJSONTyped(json, false);
}
function AgentRegionSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    rateType: json["rateType"] == null ? void 0 : json["rateType"],
    paymentType: json["paymentType"] == null ? void 0 : json["paymentType"],
    commission: json["commission"] == null ? void 0 : json["commission"]
  };
}

// src/rentals-api-v3/models/LeadTime1.ts
function LeadTime1FromJSON(json) {
  return LeadTime1FromJSONTyped(json, false);
}
function LeadTime1FromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeCode: json["fleetTypeCode"] == null ? void 0 : json["fleetTypeCode"],
    leadTime: json["leadTime"] == null ? void 0 : json["leadTime"]
  };
}

// src/rentals-api-v3/models/AgentSettings.ts
function AgentSettingsFromJSON(json) {
  return AgentSettingsFromJSONTyped(json, false);
}
function AgentSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    au: AgentRegionSettingsFromJSON(json["au"]),
    nz: AgentRegionSettingsFromJSON(json["nz"]),
    cancellationMinimum: json["cancellationMinimum"] == null ? void 0 : json["cancellationMinimum"],
    leadTimes: json["leadTimes"] == null ? void 0 : json["leadTimes"].map(LeadTime1FromJSON)
  };
}

// src/rentals-api-v3/models/AgentInfo.ts
function AgentInfoFromJSON(json) {
  return AgentInfoFromJSONTyped(json, false);
}
function AgentInfoFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    name: json["name"],
    code: json["code"],
    parent: json["parent"] == null ? void 0 : json["parent"],
    branch: json["branch"] == null ? void 0 : json["branch"],
    type: json["type"] == null ? void 0 : json["type"],
    logo: json["logo"] == null ? void 0 : json["logo"],
    settings: json["settings"] == null ? void 0 : AgentSettingsFromJSON(json["settings"]),
    features: json["features"] == null ? void 0 : json["features"]
  };
}

// src/rentals-api-v3/runtime.ts
import { formatISO } from "date-fns";
var BASE_PATH = "https://rentals.api.jucy.cloud".replace(/\/+$/, "");
var Configuration = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi || fetch;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig = new Configuration();
async function createResponseErrorMessage(response, fallback = "Response returned an error code") {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.startsWith("application/json")) {
      const json = await response.json();
      return new ResponseError(response, json?.message || json?.Message || response.statusText || fallback, json);
    }
    const body = await response.text();
    return new ResponseError(response, body || response.statusText || fallback, { body });
  } catch (e) {
    return new ResponseError(response, fallback, { error: e });
  }
}
var BaseAPI = class _BaseAPI {
  constructor(configuration = DefaultConfig) {
    this.configuration = configuration;
    this.middleware = configuration.middleware;
  }
  static jsonRegex = new RegExp("^(:?application/json|[^;/ 	]+/[^;/ 	]+[+]json)[ 	]*(:?;.*)?$", "i");
  middleware;
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    if (!mime) {
      return false;
    }
    return _BaseAPI.jsonRegex.test(mime);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && response.status >= 200 && response.status < 300) {
      return response;
    }
    throw await createResponseErrorMessage(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overriddenInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    let body;
    if (isFormData(overriddenInit.body) || overriddenInit.body instanceof URLSearchParams || isBlob(overriddenInit.body)) {
      body = overriddenInit.body;
    } else if (this.isJsonMime(headers["Content-Type"])) {
      body = JSON.stringify(overriddenInit.body);
    } else {
      body = overriddenInit.body;
    }
    const init = {
      ...overriddenInit,
      body
    };
    return { url, init };
  }
  fetchApi = async (url, init) => {
    let fetchParams = { url, init };
    for (const middleware of this.middleware) {
      if (middleware.pre) {
        fetchParams = await middleware.pre({
          fetch: this.fetchApi,
          ...fetchParams
        }) || fetchParams;
      }
    }
    let response = void 0;
    try {
      response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
    } catch (e) {
      for (const middleware of this.middleware) {
        if (middleware.onError) {
          response = await middleware.onError({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            error: e,
            response: response ? response.clone() : void 0
          }) || response;
        }
      }
      if (response === void 0) {
        if (e instanceof Error) {
          throw new FetchError(e, "The request failed and the interceptors did not return an alternative response");
        } else {
          throw e;
        }
      }
    }
    for (const middleware of this.middleware) {
      if (middleware.post) {
        response = await middleware.post({
          fetch: this.fetchApi,
          url: fetchParams.url,
          init: fetchParams.init,
          response: response.clone()
        }) || response;
      }
    }
    return response;
  };
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
var ResponseError = class extends Error {
  constructor(response, msg, data) {
    super(msg);
    this.response = response;
    this.data = data;
  }
  name = "ResponseError";
  data;
};
var FetchError = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
  }
  name = "FetchError";
};
var RequiredError = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
  }
  name = "RequiredError";
};
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
  }
  if (value instanceof Object) {
    return querystring(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
var JSONApiResponse = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};
var TextApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.text();
  }
};

// src/rentals-api-v3/apis/AgentApi.ts
var AgentApi = class extends BaseAPI {
  /**
   * Get information about an agent account
   */
  async getAgentDetailsRaw(requestParameters, initOverrides) {
    if (requestParameters["id"] == null) {
      throw new RequiredError("id", 'Required parameter "id" was null or undefined when calling getAgentDetails().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/agent/{id}`;
    urlPath = urlPath.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters["id"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => AgentInfoFromJSON(jsonValue));
  }
  /**
   * Get information about an agent account
   */
  async getAgentDetails(id, initOverrides) {
    const response = await this.getAgentDetailsRaw({ id }, initOverrides);
    return await response.value();
  }
  /**
   * Download agent rates file
   */
  async getAgentRatesRaw(requestParameters, initOverrides) {
    if (requestParameters["region"] == null) {
      throw new RequiredError("region", 'Required parameter "region" was null or undefined when calling getAgentRates().');
    }
    if (requestParameters["id"] == null) {
      throw new RequiredError("id", 'Required parameter "id" was null or undefined when calling getAgentRates().');
    }
    const queryParameters = {};
    if (requestParameters["region"] != null) {
      queryParameters["region"] = requestParameters["region"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/agent/{id}/rates`;
    urlPath = urlPath.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters["id"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    if (this.isJsonMime(response.headers.get("content-type"))) {
      return new JSONApiResponse(response);
    } else {
      return new TextApiResponse(response);
    }
  }
  /**
   * Download agent rates file
   */
  async getAgentRates(region, id, initOverrides) {
    const response = await this.getAgentRatesRaw({ region, id }, initOverrides);
    return await response.value();
  }
};
export {
  AgentApi
};
//# sourceMappingURL=AgentApi.mjs.map